import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { SelectBox } from '../molecules/select-box';

import { RelayControl } from '../shared';
import { useSelector } from '../store/utils';
import { SpotPricePeriod } from '../../../core/src/shared/enums';

type Props = {
  index: number;
  morePlugsEnabled: boolean;
  thermostatInstall: boolean;
};

export const RelayControlTypeSelect = ({
  index,
  morePlugsEnabled,
  thermostatInstall,
}: Props) => {
  const spPricePeriod = useSelector((s) => s.variables.SP_PRICE_PERIOD);
  const relayControlTypeOptions: Record<
    RelayControl,
    { label: ReactNode; visible: boolean }
  > = {
    [RelayControl.None]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.none"
          defaultMessage="Není použito, trvale rozepnuto"
        />
      ),
      visible: true,
    },
    [RelayControl.Active]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.Active"
          defaultMessage="Trvale sepnuto"
        />
      ),
      visible: true,
    },
    [RelayControl.Plug1]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.plug1"
          defaultMessage="Zásuvka 1"
        />
      ),
      visible: true,
    },
    [RelayControl.Plug2]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.plug2"
          defaultMessage="Zásuvka 2"
        />
      ),
      visible: true,
    },
    [RelayControl.Plug3]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.plug3"
          defaultMessage="Zásuvka 3"
        />
      ),
      visible: true,
    },
    [RelayControl.Plug4]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.plug4"
          defaultMessage="Zásuvka 4"
        />
      ),
      visible: morePlugsEnabled,
    },
    [RelayControl.SpotHi]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotHi"
          defaultMessage="Vysoká cena spotu (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotHi15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotHi15M"
          defaultMessage="Vysoká cena spotu (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotMi]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotMi"
          defaultMessage="Střední cena spotu (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotMi15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotMi15M"
          defaultMessage="Střední cena spotu (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotLow]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotLow"
          defaultMessage="Nízká cena spotu (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotLow15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotLow15M"
          defaultMessage="Nízká cena spotu (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotNegative]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotNegative"
          defaultMessage="Zákaz přetoků do sítě (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotNegative15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotNegative15M"
          defaultMessage="Zákaz přetoků do sítě (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotPriceBelow]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotPriceBelow"
          defaultMessage="Cena spotu pod nastavenou hodnotu (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotPriceBelow15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotPriceBelow15M"
          defaultMessage="Cena spotu pod nastavenou hodnotu (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotPriceAbove]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotPriceAbove"
          defaultMessage="Cena spotu nad nastavenou hodnotu (60 min)"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotPriceAbove15M]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotPriceAbove15M"
          defaultMessage="Cena spotu nad nastavenou hodnotu (15 min)"
        />
      ),
      visible: spPricePeriod === SpotPricePeriod['15M'],
    },
    [RelayControl.SpotBatteryDischarge]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotBatteryDischarge"
          defaultMessage="Vybíjení baterie do sítě"
        />
      ),
      visible: true,
    },
    [RelayControl.SpotStopAccumulation]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.spotStopAccumulation"
          defaultMessage="Akumulace zastavena, aktivní prodej"
        />
      ),
      visible: true,
    },
    [RelayControl.HDO]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.hdo"
          defaultMessage="Zrcadlit HDO"
        />
      ),
      visible: true,
    },
    [RelayControl.BatteryLow]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.batteryLow"
          defaultMessage="Nabití baterie pod nastavené minimum"
        />
      ),
      visible: true,
    },
    [RelayControl.BatteryBelowSOC]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.batteryBelowSOC"
          defaultMessage="Nabití baterie pod nastavené SOC"
        />
      ),
      visible: true,
    },
    [RelayControl.BatteryFull]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.batteryFull"
          defaultMessage="Nabití baterie nad nastavené SOC"
        />
      ),
      visible: true,
    },
    [RelayControl.HeatWaterMaxTemp]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.heatWaterMaxTemp"
          defaultMessage="Bojler je téměř natopený"
        />
      ),
      visible: true,
    },
    [RelayControl.HeatWaterBelowTemp]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.heatWaterBelowTemp"
          defaultMessage="Bojler je natopený pod nastavenou teplotu"
        />
      ),
      visible: true,
    },
    [RelayControl.HeatWaterAboveTemp]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.heatWaterTemp"
          defaultMessage="Bojler je natopený nad nastavenou teplotu"
        />
      ),
      visible: true,
    },
    [RelayControl.Thermostat1]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.thermostat1"
          defaultMessage="Termostat 1"
        />
      ),
      visible: thermostatInstall,
    },
    [RelayControl.Thermostat2]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.thermostat2"
          defaultMessage="Termostat 2"
        />
      ),
      visible: thermostatInstall,
    },
    [RelayControl.Thermostat3]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.thermostat3"
          defaultMessage="Termostat 3"
        />
      ),
      visible: thermostatInstall,
    },
    [RelayControl.Thermostat4]: {
      label: (
        <FormattedMessage
          id="settings.relay.type.thermostat4"
          defaultMessage="Termostat 4"
        />
      ),
      visible: thermostatInstall,
    },
  };

  return (
    <SelectBox
      title={
        <FormattedMessage
          id="settings.relay.option"
          defaultMessage="Podmínka"
        />
      }
      visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
      selectVariableId={`RELAY_CONTROL_TYPE[0][${index}]`}
      options={Object.entries(relayControlTypeOptions)
        .filter(([, controlType]) => controlType.visible)
        .map(([value, controlType]) => ({ label: controlType.label, value }))}
      fullWidth
    />
  );
};

export const relayControlTypeLabels: Record<RelayControl, ReactNode> = {
  [RelayControl.None]: (
    <FormattedMessage
      id="settings.relay.type.none"
      defaultMessage="Není použito, trvale rozepnuto"
    />
  ),
  [RelayControl.Active]: (
    <FormattedMessage
      id="settings.relay.type.Active"
      defaultMessage="Trvale sepnuto"
    />
  ),
  [RelayControl.Plug1]: (
    <FormattedMessage
      id="settings.relay.type.plug1"
      defaultMessage="Zásuvka 1"
    />
  ),
  [RelayControl.Plug2]: (
    <FormattedMessage
      id="settings.relay.type.plug2"
      defaultMessage="Zásuvka 2"
    />
  ),
  [RelayControl.Plug3]: (
    <FormattedMessage
      id="settings.relay.type.plug3"
      defaultMessage="Zásuvka 3"
    />
  ),
  [RelayControl.Plug4]: (
    <FormattedMessage
      id="settings.relay.type.plug4"
      defaultMessage="Zásuvka 4"
    />
  ),
  [RelayControl.SpotHi]: (
    <FormattedMessage
      id="settings.relay.type.spotHi"
      defaultMessage="Vysoká cena spotu (60 min)"
    />
  ),
  [RelayControl.SpotMi]: (
    <FormattedMessage
      id="settings.relay.type.spotMi"
      defaultMessage="Střední cena spotu (60 min)"
    />
  ),
  [RelayControl.SpotLow]: (
    <FormattedMessage
      id="settings.relay.type.spotLow"
      defaultMessage="Nízká cena spotu (60 min)"
    />
  ),
  [RelayControl.SpotNegative]: (
    <FormattedMessage
      id="settings.relay.type.spotNegative"
      defaultMessage="Zákaz přetoků do sítě (60 min)"
    />
  ),
  [RelayControl.SpotPriceBelow]: (
    <FormattedMessage
      id="settings.relay.type.spotPriceBelow"
      defaultMessage="Cena spotu pod nastavenou hodnotu (60 min)"
    />
  ),
  [RelayControl.SpotPriceAbove]: (
    <FormattedMessage
      id="settings.relay.type.spotPriceAbove"
      defaultMessage="Cena spotu nad nastavenou hodnotu (60 min)"
    />
  ),
  [RelayControl.SpotHi15M]: (
    <FormattedMessage
      id="settings.relay.type.spotHi15M"
      defaultMessage="Vysoká cena spotu (15 min)"
    />
  ),
  [RelayControl.SpotMi15M]: (
    <FormattedMessage
      id="settings.relay.type.spotMi15M"
      defaultMessage="Střední cena spotu (15 min)"
    />
  ),
  [RelayControl.SpotLow15M]: (
    <FormattedMessage
      id="settings.relay.type.spotLow15M"
      defaultMessage="Nízká cena spotu (15 min)"
    />
  ),
  [RelayControl.SpotNegative15M]: (
    <FormattedMessage
      id="settings.relay.type.spotNegative15M"
      defaultMessage="Zákaz přetoků do sítě (15 min)"
    />
  ),
  [RelayControl.SpotPriceBelow15M]: (
    <FormattedMessage
      id="settings.relay.type.spotPriceBelow15M"
      defaultMessage="Cena spotu pod nastavenou hodnotu (15 min)"
    />
  ),
  [RelayControl.SpotPriceAbove15M]: (
    <FormattedMessage
      id="settings.relay.type.spotPriceAbove15M"
      defaultMessage="Cena spotu nad nastavenou hodnotu (15 min)"
    />
  ),
  [RelayControl.SpotBatteryDischarge]: (
    <FormattedMessage
      id="settings.relay.type.batteryDischarge"
      defaultMessage="Vybíjení baterie do sítě"
    />
  ),
  [RelayControl.SpotStopAccumulation]: (
    <FormattedMessage
      id="settings.relay.type.stopAccumulation"
      defaultMessage="Akumulace zastavena, aktivní prodej"
    />
  ),
  [RelayControl.HDO]: (
    <FormattedMessage
      id="settings.relay.type.HDO"
      defaultMessage="Zrcadlit HDO"
    />
  ),
  [RelayControl.BatteryLow]: (
    <FormattedMessage
      id="settings.relay.type.batteryLow"
      defaultMessage="Nabití baterie pod nastavené minimum"
    />
  ),
  [RelayControl.BatteryBelowSOC]: (
    <FormattedMessage
      id="settings.relay.type.batteryBelowSOC"
      defaultMessage="Nabití baterie pod nastavené SOC"
    />
  ),
  [RelayControl.BatteryFull]: (
    <FormattedMessage
      id="settings.relay.type.batteryFull"
      defaultMessage="Nabití baterie nad nastavené SOC"
    />
  ),
  [RelayControl.HeatWaterMaxTemp]: (
    <FormattedMessage
      id="settings.relay.type.bojlerFull"
      defaultMessage="Bojler je téměř natopený"
    />
  ),
  [RelayControl.HeatWaterBelowTemp]: (
    <FormattedMessage
      id="settings.relay.type.heatWaterBelowTemp"
      defaultMessage="Bojler je natopený pod nastavenou teplotu"
    />
  ),
  [RelayControl.HeatWaterAboveTemp]: (
    <FormattedMessage
      id="settings.relay.type.heatWaterAboveTemp"
      defaultMessage="Bojler je natopený nad nastavenou teplotu"
    />
  ),
  [RelayControl.Thermostat1]: (
    <FormattedMessage
      id="settings.relay.type.thermostat1"
      defaultMessage="Termostat 1"
    />
  ),
  [RelayControl.Thermostat2]: (
    <FormattedMessage
      id="settings.relay.type.thermostat2"
      defaultMessage="Termostat 2"
    />
  ),
  [RelayControl.Thermostat3]: (
    <FormattedMessage
      id="settings.relay.type.thermostat3"
      defaultMessage="Termostat 3"
    />
  ),
  [RelayControl.Thermostat4]: (
    <FormattedMessage
      id="settings.relay.type.thermostat4"
      defaultMessage="Termostat 4"
    />
  ),
};
